import { ProblemCombinationTextType, LocationType } from 'types/LocationTypes';
import { Order } from 'types/OrderTypes';
import { FormType, FROM_STATION, StationFieldType, StationsSearchService, TO_STATION } from './StationSearchService';

export type StationFormValidationContext = {
  stopPlaces: LocationType[];
  problemCombinationTexts: ProblemCombinationTextType;
  sameStationError: string;
};

export type StationFormAction =
  | {
      type: 'hydrate';
      order: Order;
      context: StationFormValidationContext;
    }
  | {
      type: 'set-values';
      values: Partial<Record<string, string>>;
      context: StationFormValidationContext;
    }
  | {
      type: 'swap';
      context: StationFormValidationContext;
    }
  | {
      type: 'revalidate';
      context: StationFormValidationContext;
    };

const cloneForm = (form: FormType): FormType =>
  new Map(Array.from(form.entries(), ([name, field]) => [name, { ...field }]));

export const validateStationForm = (form: FormType, context: StationFormValidationContext): FormType => {
  const validatedForm = cloneForm(form);
  const fromField = validatedForm.get(FROM_STATION);
  const toField = validatedForm.get(TO_STATION);

  fromField.error = undefined;
  fromField.serviceMessage = undefined;
  toField.error = undefined;
  toField.serviceMessage = undefined;

  const isSameStation = StationsSearchService.isSameStation(fromField.value, toField.value);
  fromField.stationExists = StationsSearchService.isValidStation(fromField.value, context.stopPlaces);
  toField.stationExists = StationsSearchService.isValidStation(toField.value, context.stopPlaces);

  if (isSameStation) {
    fromField.error = context.sameStationError;
    toField.error = context.sameStationError;
  }

  if (fromField.value && toField.value) {
    const messageIds = StationsSearchService.getNoServiceMessages(fromField.value, toField.value, context.stopPlaces);
    if (messageIds) {
      StationsSearchService.setServiceMessages(messageIds, context.problemCombinationTexts, validatedForm);
    }
  }

  Array.from(validatedForm.values()).forEach((field: StationFieldType) => {
    field.isValid = !field.error && !field.serviceMessage && field.stationExists;
  });

  return validatedForm;
};

const setStationValues = (
  form: FormType,
  values: Partial<Record<string, string>>,
  context: StationFormValidationContext,
): FormType => {
  const updatedForm = cloneForm(form);

  Object.entries(values).forEach(([name, value]) => {
    const field = updatedForm.get(name);
    if (field && value !== undefined) {
      field.value = value;
    }
  });

  return validateStationForm(updatedForm, context);
};

export const stationFormReducer = (form: FormType, action: StationFormAction): FormType => {
  switch (action.type) {
    case 'hydrate':
      return setStationValues(
        form,
        {
          [FROM_STATION]: action.order.from?.name || '',
          [TO_STATION]: action.order.to?.name || '',
        },
        action.context,
      );
    case 'set-values':
      return setStationValues(form, action.values, action.context);
    case 'swap':
      return setStationValues(
        form,
        {
          [FROM_STATION]: form.get(TO_STATION).value,
          [TO_STATION]: form.get(FROM_STATION).value,
        },
        action.context,
      );
    case 'revalidate':
      return validateStationForm(form, action.context);
  }
};
